import * as React from 'react';

import { Result } from 'core/infrastructure';
import { Res } from 'core/api';
import { Environment } from 'core/utils';

import { Row, Column } from './Grid';
import * as _Form from './Form';
import { ColumnFilter, createFilters, getPossibleArguments } from './DataGrid.Filter';
import { createFilterControls } from './DataGrid.FilterControl';
import { DataGridColumn } from './DataGrid.Column';
import { Dialog, DialogActions, DialogBody, DialogFooter, DialogHeader, showCustom, showMessage } from './Dialog';
import Button from './Button';


type Props<T> = {
	columns: DataGridColumn<T>[];
	currentFilters: ColumnFilter<T>[];
	allPossibleFilters: ColumnFilter<T>[];
	items: T[];
	actions?: DialogActions<ColumnFilter<T>[]>;
};

type State<T> = {
	filters: ColumnFilter<T>[];
};

class FilterDialog<T> extends React.Component<Props<T>, State<T>> {

	private form = React.createRef<_Form.Form<State<T>>>();
	private possibleArguments: { [propertyPath: string]: string[] };

	constructor(props: Props<T>) {
		super(props);

		this.onKeyDown = this.onKeyDown.bind(this);
		this.onOK = this.onOK.bind(this);
		this.onCancel = this.onCancel.bind(this);
		this.onAddFilter = this.onAddFilter.bind(this);
		this.onRemoveFilter = this.onRemoveFilter.bind(this);
		this.onRemoveAllFilters = this.onRemoveAllFilters.bind(this);

		this.possibleArguments = {};

		this.state = {
			// Clono los filtros actuales para no romper nada
			filters: props.currentFilters.map(filter => ({ ...filter }))
		};
	}

	private getCachedPossibleArguments(filter: ColumnFilter<T>): string[] {
		if (this.possibleArguments[filter.propertyPath])
			return this.possibleArguments[filter.propertyPath];

		return this.possibleArguments[filter.propertyPath] = getPossibleArguments(filter, this.props.items);
	}

	private onAddFilter() {
		this.setState({
			filters: [...this.state.filters, { ...this.props.allPossibleFilters[0], argument: '' }]
		});
	}

	private async onRemoveAllFilters() {
		this.setState({
			filters: []
		}, () => this.form.current!.remountAll());
	}

	private async onRemoveFilter(filter: ColumnFilter<T>) {
		await this.form.current!.updateModel();
		this.setState({
			filters: this.state.filters.filter(x => x !== filter)
		}, () => this.form.current!.remountAll());
	}

	private async onOK() {
		await this.form.current!.ifValid().then(() => this.props.actions && this.props.actions.closeWith(this.state.filters));
	}

	private onCancel() {
		this.props.actions?.cancel();
	}

	private async onKeyDown(e: React.KeyboardEvent<any>) {

		// Si no lo hacemos nos lanza el siguiente error: 
		// This synthetic event is reused for performance reasons.If you're seeing this, you're accessing the method`keyCode` on a released / nullified synthetic event.This is a no - op function.If you must keep the original synthetic event around, use event.persist()
		e.persist();

		// Tengo unos fantásticos métodos de ayuda para hacer esto en UI, pero no puedo usarlos
		// porque introduciría una dependencia circular

		// En el caso de android, cuando el type no es text, en lugar de un botón de Enter (Ir)
		// muestra un botón de Siguiente (Sig) equivalente al tab, lo que imposibilita al usuario
		// pulsar Enter. Para ñapearlo, si es android (y sólo si es android) equiparo el tab al
		// Enter
		if (e.keyCode === 13 || (Environment.isAndroid() && e.keyCode === 9)) {
			e.preventDefault();
			e.stopPropagation();
			await this.onOK();
		}

		if (e.keyCode === 27) {
			e.preventDefault();
			e.stopPropagation();
			this.onCancel();
		}
	}

	render() {
		const Form: _Form.FormCtor<State<T>> = _Form.Form;

		return <Dialog kind='large' onKeyDown={this.onKeyDown}>
			<DialogHeader>
				<h4 className='modal-title'>{Res.WA.WA_DATAGRID_FILTER()}</h4>
			</DialogHeader>
			<DialogBody>
				<Form model={this.state} ref={this.form}>
					{this.state.filters.map((filter, idx) => createFilterControls({
						pathPrefix: `filters[${idx}]`,
						filter,
						allPossibleFilters: this.props.allPossibleFilters,
						possibleArguments: this.getCachedPossibleArguments(filter),
						columns: this.props.columns,
						onChange: async () => {
							await this.form.current!.remount(`filters[${idx}].propertyPath`, `filters[${idx}].operator`, `filters[${idx}].argument`);
							this.forceUpdate();
						},
						onRemove: () => this.onRemoveFilter(filter)
					}))}
				</Form>

				{this.state.filters.length ? null : <h3 className='font-trans m-b-lg'>{Res.WA.WA_DATAGRID_FILTER_NO_FILTER()}</h3>}

				<Row>
					<Column width='one-quarter'>
						<Button onClick={this.onAddFilter}><i className='text-info fa fa-plus'></i> {Res.Common.ADD_BTN()}</Button>
					</Column>
					<Column width='one-half'></Column>
					<Column width='one-quarter'>
						<Button onClick={this.onRemoveAllFilters}><i className='text-danger fa fa-minus'></i> {Res.WA.WA_DATAGRID_FILTER_REMOVE_ALL()}</Button>
					</Column>
				</Row>
			</DialogBody>
			<DialogFooter>
				<Button buttonStyle='info' onClick={this.onOK}>{Res.Common.OK_BTN()}</Button>
				<Button onClick={this.onCancel}>{Res.Common.CANCEL_BTN()}</Button>
			</DialogFooter>
		</Dialog>;
	}
}

export async function configureFilter<T>(currentFilters: ColumnFilter<T>[], columns: DataGridColumn<T>[], items: T[]): Promise<Result<ColumnFilter<T>[]>> {
	const allPossibleFilters = columns.flatMap(column => createFilters(column, items));

	if (allPossibleFilters.length === 0) {
		await showMessage(Res.Msg.MSG_CANNOT_APPLY_FILTER_WITH_CURRENT_DATA(), Res.Common.WARNING());
		return Result.error();
	}

	return showCustom<ColumnFilter<T>[]>(<FilterDialog {...{ currentFilters, columns, items, allPossibleFilters }} />);
}
